import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ChevronLeft, ChevronRight, Search, RotateCcw } from "lucide-react";

const ACTION_LABELS = {
  "order.stage_change": "Aşama Değişikliği",
  "order.cancel": "İptal / Red",
  "line.stage_change": "Kalem Aşama Değişikliği",
  "invoice.create": "Fatura Oluşturma",
  "invoice.bh_sent": "Bizim Hesap'a Gönderildi",
  "invoice.bh_send_failed": "Bizim Hesap Gönderim Hatası",
  "stock.adjust": "Stok Düzeltme",
  "stock.transfer": "Depo Transferi",
  "stock.count": "Stok Sayımı",
  "payment.create": "Ödeme/Tahsilat Ekleme",
  "payment.delete": "Ödeme/Tahsilat Silme",
};
const ALL = "__all__";
const PAGE_SIZE = 50;
const EMPTY = { date_from: "", date_to: "", actor: ALL, module: ALL, action: ALL, doc_no: "" };

const fmtVal = (v) => (v === null || v === undefined ? "—" : typeof v === "object"
  ? Object.entries(v).map(([k, x]) => `${k}: ${x ?? "—"}`).join(", ") : String(v));

function ChangeCell({ it }) {
  const lines = it.meta?.lines;
  return (
    <div className="space-y-0.5">
      {(it.old || it.new) && (
        <div className="font-mono" data-testid={`audit-change-${it.id}`}>
          <span className="text-rose-400">{fmtVal(it.old)}</span>
          <span className="mx-1 text-muted-foreground">→</span>
          <span className="text-emerald-400">{fmtVal(it.new)}</span>
        </div>
      )}
      {Array.isArray(lines) && lines.slice(0, 3).map((l, i) => (
        <div key={i} className="text-muted-foreground">
          {l.product_name || l.product_id?.slice(0, 8)}{l.old !== undefined ? `: ${l.old} → ${l.new}` : ` × ${l.quantity}`}
        </div>
      ))}
      {Array.isArray(lines) && lines.length > 3 && <div className="text-muted-foreground">+{lines.length - 3} satır</div>}
      {!lines && it.meta && Object.keys(it.meta).length > 0 && !it.old && !it.new && (
        <div className="text-muted-foreground font-mono">{JSON.stringify(it.meta).slice(0, 120)}</div>
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 text-xs" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function AuditFilters({ f, setF, opts, onApply, onReset }) {
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  return (
    <Card className="rounded-sm p-3 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2 items-end" data-testid="audit-filters">
      <div><div className="text-[10px] uppercase font-mono text-muted-foreground mb-1">Başlangıç</div>
        <Input type="date" className="h-9 text-xs" value={f.date_from} onChange={set("date_from")} data-testid="audit-filter-date-from" /></div>
      <div><div className="text-[10px] uppercase font-mono text-muted-foreground mb-1">Bitiş</div>
        <Input type="date" className="h-9 text-xs" value={f.date_to} onChange={set("date_to")} data-testid="audit-filter-date-to" /></div>
      <FilterSelect value={f.actor} onChange={set("actor")} placeholder="Tüm Kullanıcılar" testId="audit-filter-actor"
        options={opts.actors.map((a) => ({ value: a, label: a }))} />
      <FilterSelect value={f.module} onChange={set("module")} placeholder="Tüm Modüller" testId="audit-filter-module"
        options={opts.modules.map((m) => ({ value: m.code, label: m.label }))} />
      <FilterSelect value={f.action} onChange={set("action")} placeholder="Tüm İşlemler" testId="audit-filter-action"
        options={opts.actions.map((a) => ({ value: a, label: ACTION_LABELS[a] || a }))} />
      <Input className="h-9 text-xs" placeholder="Belge No ara…" value={f.doc_no} onChange={set("doc_no")}
        onKeyDown={(e) => e.key === "Enter" && onApply()} data-testid="audit-filter-doc-no" />
      <div className="flex gap-2">
        <Button size="sm" className="h-9 flex-1" onClick={onApply} data-testid="audit-filter-apply"><Search className="w-3.5 h-3.5 mr-1" />Filtrele</Button>
        <Button size="sm" variant="outline" className="h-9" onClick={onReset} data-testid="audit-filter-reset"><RotateCcw className="w-3.5 h-3.5" /></Button>
      </div>
    </Card>
  );
}

export default function AuditTab() {
  const [f, setF] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [opts, setOpts] = useState({ modules: [], actors: [], actions: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/audit-log/filters").then((r) => setOpts(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE };
    Object.entries(applied).forEach(([k, v]) => { if (v && v !== ALL) params[k] = v; });
    api.get("/audit-log", { params }).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 })).finally(() => setLoading(false));
  }, [applied, page]);
  useEffect(() => { load(); }, [load]);

  const modLabel = Object.fromEntries(opts.modules.map((m) => [m.code, m.label]));
  const pages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  const apply = () => { setPage(1); setApplied({ ...f }); };
  const reset = () => { setF(EMPTY); setPage(1); setApplied(EMPTY); };

  return (
    <div className="space-y-3" data-testid="audit-tab">
      <AuditFilters f={f} setF={setF} opts={opts} onApply={apply} onReset={reset} />
      <div className="text-xs uppercase font-mono text-muted-foreground" data-testid="audit-total">Toplam {data.total || 0} kayıt</div>
      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : data.items.length === 0 ? (
        <Card className="rounded-sm border-dashed p-10 text-center text-sm text-muted-foreground" data-testid="audit-empty">Kayıt bulunamadı.</Card>
      ) : (
        <div className="border border-border rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="bg-muted/40 border-b border-border">
              <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                {["Tarih", "Kullanıcı", "Modül", "İşlem", "Belge No", "Eski → Yeni / Detay"].map((h) => <th key={h} className="text-left px-3 py-2 whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.items.map((it) => (
                <tr key={it.id} data-testid={`audit-row-${it.id}`} className="align-top">
                  <td className="px-3 py-1.5 text-xs font-mono whitespace-nowrap">{new Date(it.created_at).toLocaleString("tr-TR")}</td>
                  <td className="px-3 py-1.5 text-xs">{it.actor_email}</td>
                  <td className="px-3 py-1.5 text-xs whitespace-nowrap">{modLabel[it.module || "users"] || it.module || "Kullanıcı/Rol"}</td>
                  <td className="px-3 py-1.5"><span className="text-xs font-mono bg-muted border border-border rounded-sm px-1.5 py-0.5 whitespace-nowrap">{ACTION_LABELS[it.action] || it.action}</span></td>
                  <td className="px-3 py-1.5 text-xs font-mono whitespace-nowrap">{it.doc_no || `${it.target_type || ""} · ${it.target_id?.slice(0, 8) || ""}`}</td>
                  <td className="px-3 py-1.5 text-xs"><ChangeCell it={it} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex items-center justify-end gap-2" data-testid="audit-pagination">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="audit-prev-page"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-xs font-mono" data-testid="audit-page-info">{page} / {pages}</span>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="audit-next-page"><ChevronRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
